// src/hooks/useOrgEmailStatus.ts

// What? Shared hook that reports whether an organization's email sending is
//       enabled (the per-org `email_enabled` kill switch on the email config).
// Why?  The admin UI needs to dim (not hide) interactive controls when an org
//       has disabled email, and propagate toggle changes across peer cards on
//       the same page without a navigation.
// How?  Module-level cache keyed by orgId. First consumer per org triggers a
//       fetch; subsequent consumers read from the cache. `invalidate` clears
//       the entry and re-fetches, broadcasting `loading: true` in the meantime
//       so consumers never render the stale value during the re-fetch window.

import { useEffect, useState } from "react";
import { useAuthFetch } from "../utils/useAuthFetch";

export type OrgEmailStatus = {
  enabled: boolean;
  loading: boolean;
  error: string | null;
};

type FetcherFn = (
  endpoint: string,
  tag: string,
  options?: RequestInit,
  timeout?: number,
) => Promise<any>;

type CacheEntry = {
  status: OrgEmailStatus;
  subscribers: Set<(status: OrgEmailStatus) => void>;
  inflight: boolean;
};

const cache = new Map<string, CacheEntry>();

const SAFE_DEFAULT: OrgEmailStatus = {
  enabled: true,
  loading: false,
  error: null,
};

function getOrCreateEntry(orgId: string): CacheEntry {
  let entry = cache.get(orgId);
  if (!entry) {
    entry = {
      status: { enabled: true, loading: true, error: null },
      subscribers: new Set(),
      inflight: false,
    };
    cache.set(orgId, entry);
  }
  return entry;
}

function broadcast(entry: CacheEntry, next: OrgEmailStatus) {
  entry.status = next;
  entry.subscribers.forEach((cb) => cb(next));
}

async function fetchStatus(orgId: string, authFetch: FetcherFn): Promise<void> {
  const entry = getOrCreateEntry(orgId);
  if (entry.inflight) return;
  entry.inflight = true;

  // Ensure subscribers see a loading state while the request is in flight.
  broadcast(entry, {
    enabled: entry.status.enabled,
    loading: true,
    error: null,
  });

  try {
    const data = await authFetch(
      `/api/email/organization/${orgId}/config`,
      "fetch email status",
    );

    // Backend returns `{ configured: boolean, config: {...} }`. When a config
    // is saved, `email_enabled` lives on `config`. When no config is saved
    // yet, treat email as enabled (matches the backend default behaviour).
    let enabled = true;
    if (data?.configured && data?.config) {
      enabled = data.config.email_enabled !== false;
    }
    broadcast(entry, { enabled, loading: false, error: null });
  } catch (err) {
    // Degrade to "enabled" on transient failures so admins are never locked
    // out by a flaky network request. Surface the error for display.
    const message = err instanceof Error ? err.message : String(err);
    broadcast(entry, { enabled: true, loading: false, error: message });
  } finally {
    entry.inflight = false;
  }
}

export function invalidateOrgEmailStatus(organizationId: string): void {
  const entry = cache.get(organizationId);
  if (!entry) return;
  // Flip to loading immediately so every live subscriber stops rendering the
  // stale `enabled` value while the re-fetch is in flight.
  broadcast(entry, {
    enabled: entry.status.enabled,
    loading: true,
    error: null,
  });
  // Let each subscriber trigger its own re-fetch via its authFetch instance.
  // Subscribers register a one-shot refresh trigger; see useOrgEmailStatus.
  entry.subscribers.forEach((cb) => {
    (cb as any).__refresh?.();
  });
}

export function useOrgEmailStatus(
  organizationId: string | undefined,
  isAdmin = true,
): OrgEmailStatus {
  const authFetch = useAuthFetch();
  const [status, setStatus] = useState<OrgEmailStatus>(() => {
    if (!organizationId) return SAFE_DEFAULT;
    const entry = cache.get(organizationId);
    return entry ? entry.status : { enabled: true, loading: true, error: null };
  });

  useEffect(() => {
    if (!organizationId || !isAdmin) {
      setStatus(SAFE_DEFAULT);
      return;
    }

    const entry = getOrCreateEntry(organizationId);
    const subscriber = (next: OrgEmailStatus) => setStatus(next);
    // Tag each subscriber with a refresh trigger the invalidate path can call.
    (subscriber as any).__refresh = () => {
      fetchStatus(organizationId, authFetch);
    };
    entry.subscribers.add(subscriber);
    // Surface the current cached value to this consumer right away.
    setStatus(entry.status);

    // First mount for this org — kick off the initial fetch.
    if (entry.status.loading && !entry.inflight) {
      fetchStatus(organizationId, authFetch);
    }

    return () => {
      entry.subscribers.delete(subscriber);
    };
  }, [organizationId, isAdmin, authFetch]);

  return status;
}
